import { useState, useCallback, useRef } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Box,
} from '../../../base-components';
import {
    EditorContainer,
    CropContainer,
} from './ImageEditor.styles';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { useTranslation } from 'react-i18next';
import {
    getImageFileName,
    ImageOutputFormat,
} from '../utils/imageProcessing.utils';

interface ImageEditorProps {
    open: boolean;
    imageSrc: string;
    onClose: () => void;
    onSave: (file: File, url: string) => void;
    initialAspectRatio?: number;
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    outputFormat?: ImageOutputFormat;
}

const ImageEditor = ({
    open,
    imageSrc,
    onClose,
    onSave,
    initialAspectRatio,
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.9,
    outputFormat = 'image/jpeg',
}: ImageEditorProps) => {
    const { t } = useTranslation();
    const imgRef = useRef<HTMLImageElement>(null);
    const [crop, setCrop] = useState<Crop>({
        unit: '%',
        width: 90,
        height: 90,
        x: 5,
        y: 5,
    });
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [rotation, setRotation] = useState(0);
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0, imageWidth: 0, imageHeight: 0 });

    const getFullImageCrop = useCallback((): PixelCrop | null => {
        if (!canvasSize.imageWidth || !canvasSize.imageHeight) {
            return null;
        }

        return {
            unit: 'px',
            x: (canvasSize.width - canvasSize.imageWidth) / 2,
            y: (canvasSize.height - canvasSize.imageHeight) / 2,
            width: canvasSize.imageWidth,
            height: canvasSize.imageHeight,
        };
    }, [canvasSize]);

    const getCroppedImage = useCallback(async (): Promise<{ file: File; url: string } | null> => {
        const cropToExport =
            completedCrop && completedCrop.width > 0 && completedCrop.height > 0
                ? completedCrop
                : getFullImageCrop();

        if (!cropToExport || !imgRef.current || !canvasSize.imageWidth) {
            return null;
        }

        const image = imgRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return null;

        const scaleX = image.naturalWidth / canvasSize.imageWidth;
        const scaleY = image.naturalHeight / canvasSize.imageHeight;

        // Calculate the offset of the image within the canvas
        const imageOffsetX = (canvasSize.width - canvasSize.imageWidth) / 2;
        const imageOffsetY = (canvasSize.height - canvasSize.imageHeight) / 2;

        // Set canvas size to the crop size
        canvas.width = cropToExport.width * scaleX;
        canvas.height = cropToExport.height * scaleY;

        // Calculate crop position relative to the image
        const cropX = (cropToExport.x - imageOffsetX) * scaleX;
        const cropY = (cropToExport.y - imageOffsetY) * scaleY;

        // If crop extends beyond image, keep the extra area transparent
        if (cropX < 0 || cropY < 0 || 
            cropX + canvas.width > image.naturalWidth || 
            cropY + canvas.height > image.naturalHeight) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        // Calculate the source and destination rectangles for drawing
        const sourceX = Math.max(0, cropX);
        const sourceY = Math.max(0, cropY);
        const sourceWidth = Math.min(image.naturalWidth - sourceX, canvas.width - Math.max(0, -cropX) * scaleX);
        const sourceHeight = Math.min(image.naturalHeight - sourceY, canvas.height - Math.max(0, -cropY) * scaleY);
        
        const destX = Math.max(0, -cropX);
        const destY = Math.max(0, -cropY);

        // Draw the cropped image
        ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            destX,
            destY,
            sourceWidth,
            sourceHeight
        );

        // Resize if needed
        let finalCanvas = canvas;
        if (canvas.width > maxWidth || canvas.height > maxHeight) {
            const ratio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
            const resizedCanvas = document.createElement('canvas');
            resizedCanvas.width = canvas.width * ratio;
            resizedCanvas.height = canvas.height * ratio;
            const resizedCtx = resizedCanvas.getContext('2d');
            if (resizedCtx) {
                resizedCtx.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);
                finalCanvas = resizedCanvas;
            }
        }

        return new Promise((resolve) => {
            finalCanvas.toBlob(
                (blob) => {
                    if (!blob) return;
                    const file = new File(
                        [blob],
                        getImageFileName('cropped-image', outputFormat),
                        {
                            type: outputFormat,
                            lastModified: Date.now(),
                        }
                    );
                    const url = URL.createObjectURL(blob);
                    resolve({ file, url });
                },
                outputFormat,
                quality
            );
        });
    }, [completedCrop, canvasSize, getFullImageCrop, maxWidth, maxHeight, quality, outputFormat]);

    const handleSave = useCallback(async () => {
        try {
            const result = await getCroppedImage();
            if (result) {
                onSave(result.file, result.url);
                onClose();
            }
        } catch (e) {
            console.error('Error cropping image:', e);
        }
    }, [getCroppedImage, onSave, onClose]);

    const handleRotate = () => {
        setRotation((prev) => (prev + 90) % 360);
    };

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        let imgWidth = img.naturalWidth;
        let imgHeight = img.naturalHeight;
        
        // First, scale down the image if it exceeds max constraints
        const maxImgHeight = 460; // Max image height (leaving room for 30% padding to reach 600px)
        const maxImgWidth = 770; // Max image width (leaving room for 30% padding to reach 1000px)
        
        if (imgHeight > maxImgHeight || imgWidth > maxImgWidth) {
            const ratio = Math.min(maxImgHeight / imgHeight, maxImgWidth / imgWidth);
            imgWidth = imgWidth * ratio;
            imgHeight = imgHeight * ratio;
        }
        
        // Now canvas is 30% larger than the (possibly scaled) image
        const canvasWidth = imgWidth * 1.3;
        const canvasHeight = imgHeight * 1.3;
        const roundedCanvasWidth = Math.round(canvasWidth);
        const roundedCanvasHeight = Math.round(canvasHeight);
        const roundedImgWidth = Math.round(imgWidth);
        const roundedImgHeight = Math.round(imgHeight);
        const imageCrop: PixelCrop = {
            unit: 'px',
            x: Math.round((roundedCanvasWidth - roundedImgWidth) / 2),
            y: Math.round((roundedCanvasHeight - roundedImgHeight) / 2),
            width: roundedImgWidth,
            height: roundedImgHeight,
        };

        setCanvasSize({
            width: roundedCanvasWidth,
            height: roundedCanvasHeight,
            imageWidth: roundedImgWidth,
            imageHeight: roundedImgHeight,
        });
        setCrop(imageCrop);
        setCompletedCrop(imageCrop);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Typography variant="h6">{t('common.editImage')}</Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        💡 {t('common.dragCropHint') || 'Drag the corners/edges to resize the crop area'}
                    </Typography>
                </Box>
                <EditorContainer
                    sx={{
                        minHeight: canvasSize.height > 0 ? `${canvasSize.height + 48}px` : '400px',
                    }}
                >
                    <CropContainer
                        sx={{
                            width: canvasSize.width > 0 ? `${canvasSize.width}px` : 'auto',
                            height: canvasSize.height > 0 ? `${canvasSize.height}px` : 'auto',
                        }}
                    >
                        <ReactCrop
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={initialAspectRatio}
                            keepSelection
                        >
                            <Box
                                sx={{
                                    width: canvasSize.width > 0 ? `${canvasSize.width}px` : 'auto',
                                    height: canvasSize.height > 0 ? `${canvasSize.height}px` : 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    backgroundColor: 'transparent',
                                }}
                            >
                                <img
                                    ref={imgRef}
                                    src={imageSrc}
                                    alt="Crop"
                                    onLoad={handleImageLoad}
                                    style={{
                                        display: 'block',
                                        width: canvasSize.imageWidth > 0 ? `${canvasSize.imageWidth}px` : 'auto',
                                        height: canvasSize.imageHeight > 0 ? `${canvasSize.imageHeight}px` : 'auto',
                                        transform: `rotate(${rotation}deg)`,
                                        maxWidth: 'none',
                                        maxHeight: 'none',
                                    }}
                                />
                            </Box>
                        </ReactCrop>
                    </CropContainer>
                </EditorContainer>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                <IconButton onClick={handleRotate} size="large" color="primary">
                    <RotateRightIcon />
                </IconButton>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button onClick={onClose} variant="outlined">
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSave} variant="contained" disabled={!canvasSize.imageWidth}>
                        {t('common.save')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ImageEditor;
